/*
 * The composer: one interface (`types.ts`), one state machine and one frame every surface is built
 * from (`useComposerCore`, `ComposerFrame`), the loader that picks the surface a person writes in
 * (`Composer` — the formatted editor, lazily, or the source view `TextareaComposer`), and the
 * pieces a surface wires around them — the "@" source over the people and group slices, the
 * settings the composer follows, and `bootComposer`, which starts the outbox the send path uses.
 */

import { installOutbox, type Outbox } from "../drafts/outbox.ts"
import { appDraftStore } from "../drafts/useDraft.ts"
import { composerSettings } from "./settings.ts"

export { AttachmentTray } from "./attachments/AttachmentTray.tsx"
export type { AttachmentLimits } from "./attachments/limits.ts"
export type { StagedError, StagedFile, StagedState } from "./attachments/types.ts"
export { trayFor, TrayStore } from "./attachments/uploadManager.ts"
export type { TrayDeps } from "./attachments/uploadManager.ts"
export { useAttachments } from "./attachments/useAttachments.ts"
export type { Attachments } from "./attachments/useAttachments.ts"
export { peopleSource } from "./autocomplete/peopleSource.ts"
export type { GroupLike, PeopleSlices, PersonLike } from "./autocomplete/peopleSource.ts"
export { findTrigger, MAX_ITEMS } from "./autocomplete/trigger.ts"
export { Composer, preloadEditor } from "./Composer.tsx"
export { ComposerFrame } from "./ComposerFrame.tsx"
export type { ComposerFrameProps } from "./ComposerFrame.tsx"
export { PREVIEW_DEBOUNCE_MS, PreviewPane } from "./PreviewPane.tsx"
export {
	composerSettings,
	composerSettingsFrom,
	DEFAULT_COMPOSER_SETTINGS,
	ENTER_SENDS_KEY,
	SOURCE_MODE_KEY,
	useComposerSettings,
} from "./settings.ts"
export type { ComposerSettings } from "./settings.ts"
export { applyEdit } from "./text/edit.ts"
export type { Edit } from "./text/edit.ts"
export { caretInFencedBlock } from "./text/fence.ts"
export { continueList, indentList } from "./text/lists.ts"
export { pasteEdit, pasteToMarkdown } from "./text/paste.ts"
export { applyShortcut, shortcutFor } from "./text/shortcuts.ts"
export type { Placeholders, Shortcut } from "./text/shortcuts.ts"
export { TextareaComposer } from "./TextareaComposer.tsx"
export type { TextareaComposerProps } from "./TextareaComposer.tsx"
export type * from "./types.ts"
export { MAX_MESSAGE_CHARS, useComposerCore } from "./useComposerCore.ts"
export type {
	ComposerCore,
	ComposerCoreOptions,
	ComposerSurfaceProps,
	Failure,
} from "./useComposerCore.ts"

/**
 * Starts the outbox over the application's draft store with the undo window the user chose; called
 * once at boot. How each item leaves the device is its module's transport (`drafts/transport.ts`),
 * registered when the modules are bound.
 */
export function bootComposer(): Outbox {
	return installOutbox({
		store: appDraftStore(),
		holdMs: () => composerSettings().undoSendSeconds * 1000,
	})
}
